<template>
  <div>
    <vx-card class="mb-4">
      <div class="flex justify-between items-start flex-wrap gap-2 mb-2">
        <div>
          <h4 class="mb-0">{{ isEditMode ? model.name || $t('lesson.title') : $t('catalog.lesson.new_title') }}</h4>
          <small class="text-muted">{{ $t('catalog.lesson.subtitle') }}</small>
        </div>
        <div class="d-flex" style="gap: .5rem;">
          <!-- Só com ingresso ligado: sem isso o painel abriria vazio e sem
               explicação para a maioria das aulas, que não exige entrada. -->
          <b-button
            v-if="lessonId && model.ticket_enabled"
            v-permission="'tickets.grid'"
            variant="outline-primary"
            size="sm"
            @click="$router.push(`/ingressos/checkin/lesson/${lessonId}`)">
            <feather-icon icon="CheckSquareIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('catalog.ticket.panel') }}</span>
          </b-button>
          <b-button
            v-if="lessonId"
            v-permission="'lessons.show'"
            variant="outline-primary"
            size="sm"
            @click="$router.push(`/lessons/${lessonId}/agenda`)">
            <feather-icon icon="CalendarIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('lesson.agenda.title') }}</span>
          </b-button>
        </div>
      </div>

      <wizard-shell
        :steps="steps"
        :current="currentStep"
        :done-keys="doneKeys"
        :free-navigation="isEditMode"
        :next-disabled="nextDisabled"
        :busy="busy"
        @prev="goPrev"
        @next="goNext"
        @goto="goToStep">

        <!-- ============================== PASSO 1: SOBRE A AULA -->
        <div v-show="currentStep === 'sobre'">
          <validation-observer ref="validator" #default="{valid}">
            <b-form @submit.prevent>
              <b-form-row>
                <b-col cols="12" md="6">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.name')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.name')" label-for="name">
                      <b-form-input
                        id="name"
                        type="text"
                        class="required"
                        v-model="model.name"
                        :placeholder="$t('catalog.lesson.name_placeholder')"
                        :state="getValidationState(validationContext)"/>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="12" md="6">
                  <validation-provider
                    ref="modalityProvider"
                    #default="validationContext"
                    :name="$t('modality.label')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group label-for="modality">
                      <select-suggestion
                        ref="select_modality"
                        :max="20"
                        class="flex-grow required"
                        column="name"
                        model="Modality"
                        :label="$t('modality.label')"
                        :itemTextFormat="(item) => `${item.name}`"
                        v-model="modalitySelected"
                        :isDisabled="isEditMode"/>
                      <small class="text-muted d-block">{{ $t('catalog.lesson.modality_hint') }}</small>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <b-form-row>
                <b-col cols="12">
                  <validation-provider
                    #default="validationContext"
                    name="Pessoa Jurídica"
                    rules="required"
                    slim>
                    <select-suggestion
                      id="legal_entity_id"
                      column="company_name,trading_name,cnpj"
                      model="LegalEntity"
                      v-model="legalEntity"
                      :label="pessoaJuridicaLocked ? 'Vendedor' : 'Pessoa Jurídica'"
                      :appendClearOption="false"
                      :placeholderText="pessoaJuridicaLocked ? '' : 'Digite o nome do estabelecimento'"
                      :itemTextFormat="legalEntityItemTextFormat"
                      :isDisabled="pessoaJuridicaLocked"
                      :required="true"/>
                    <small class="text-danger">{{ validationContext.errors[0] }}</small>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <!-- Tipo da aula em cartões explicados -->
              <b-form-row class="mt-1">
                <b-col cols="12">
                  <b-form-group :label="$t('lesson.type')" label-class="required">
                    <choice-cards
                      :options="lessonTypeCards"
                      :value="lessonType"
                      :disabled="isEditMode"
                      @input="onChangeLessonType"/>
                    <small v-if="isEditMode" class="text-muted d-block mt-1">{{ $t('catalog.lesson.type_locked_hint') }}</small>
                    <small v-if="!lessonType" class="text-warning d-block mt-1">{{ $t('catalog.lesson.type_required') }}</small>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <!-- Como a aula é vendida. Fica aqui, e não no passo de preço,
                   porque a resposta decide se aquele passo existe. -->
              <b-form-row>
                <b-col cols="12">
                  <b-form-group :label="$t('catalog.lesson.sales_mode')">
                    <choice-cards
                      :options="salesModeCards"
                      :value="planOnly ? 'PLAN' : 'OPEN'"
                      @input="onChangeSalesMode"/>
                    <small class="text-muted d-block mt-1">{{ $t('catalog.lesson.sales_mode_hint') }}</small>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <!-- Evento único: data e horários -->
              <b-form-row v-if="lessonType === 'SGL'">
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.event_date')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.event_date')" label-for="event_date">
                      <b-form-input id="event_date" class="required" type="date" v-model="singleEventDate"/>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.start_time')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.start_time')" label-for="single_event_start_time">
                      <b-form-input id="single_event_start_time" class="required" type="time" v-model="singleEventStartTime"/>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.end_time')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.end_time')" label-for="single_event_end_time">
                      <b-form-input id="single_event_end_time" class="required" type="time" v-model="singleEventEndTime"/>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <!-- Recorrente não tem campo próprio: a recorrência é a grade
                   de horários do passo seguinte, e o fim dela é a validade de
                   cada faixa. `frequency` e `deadline_in_day` saíram em
                   2026-08-19 — o primeiro nunca foi cumprido pelo gerador e o
                   segundo virou `ends_on` na própria faixa. -->

              <!-- Pacote: quantidade -->
              <b-form-row v-if="lessonType === 'PKG'">
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.quantity')"
                    rules="required|integer"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.quantity')" label-for="quantity">
                      <b-input-group class="required">
                        <b-form-input id="quantity" class="required" type="number" v-model="model.quantity"/>
                      </b-input-group>
                      <small class="text-muted d-block">{{ $t('catalog.lesson.quantity_hint') }}</small>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <b-form-row>
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.duration')"
                    rules="required|integer"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.duration')" label-for="duration">
                      <b-input-group append="minutos" class="required">
                        <b-form-input id="duration" class="required" type="number" v-model="model.duration"/>
                      </b-input-group>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
                <b-col cols="auto">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.max_student')"
                    rules="required|integer"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.max_student')" label-for="max_student">
                      <b-form-input id="max_student" class="required" type="number" v-model="model.max_student"/>
                      <small class="text-muted d-block">{{ $t('catalog.lesson.max_student_hint') }}</small>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <b-form-row>
                <b-col cols="12">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.description')"
                    rules="required"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.description')" label-for="description">
                      <b-form-textarea
                        id="description"
                        class="required"
                        v-model="model.description"
                        rows="3"
                        max-rows="6"
                        :placeholder="$t('catalog.lesson.description_placeholder')"
                        :state="getValidationState(validationContext)"/>
                      <small class="text-muted d-block">{{ $t('catalog.lesson.description_hint') }}</small>
                      <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <!-- Opções raras ficam dobradas para não assustar o cadastro comum -->
              <button type="button" class="catalog-more-toggle" @click="moreOptionsOpen = !moreOptionsOpen">
                <feather-icon :icon="moreOptionsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="w-4 h-4" class="mr-50"/>
                {{ $t('catalog.lesson.more_options') }}
              </button>
              <b-form-row v-if="moreOptionsOpen" class="mt-1">
                <b-col cols="12" md="6">
                  <b-form-group label-for="rental_space">
                    <select-suggestion
                      ref="select_rental_space"
                      :max="20"
                      class="flex-grow w-full"
                      column="name"
                      model="RentalSpace"
                      :label="$t('lesson.rental_space')"
                      :itemTextFormat="(item) => `${item.name}`"
                      :appendClearOption="true"
                      v-model="rentalSpaceSelected"/>
                    <small class="text-muted d-block">{{ $t('catalog.lesson.rental_space_hint') }}</small>
                  </b-form-group>
                </b-col>
                <b-col cols="12" md="6">
                  <validation-provider
                    #default="validationContext"
                    :name="$t('lesson.min_purchase_advance_hours')"
                    rules="integer|min_value:0"
                    mode="lazy"
                    slim>
                    <b-form-group :label="$t('lesson.min_purchase_advance_hours')" label-for="min_purchase_advance_hours">
                      <b-input-group append="horas" style="max-width: 12rem;">
                        <b-form-input id="min_purchase_advance_hours" type="number" v-model="model.min_purchase_advance_hours"/>
                      </b-input-group>
                      <small class="text-muted d-block">{{ $t('catalog.lesson.advance_hint') }}</small>
                      <small class="text-danger">{{ validationContext.errors[0] }}</small>
                    </b-form-group>
                  </validation-provider>
                </b-col>
              </b-form-row>

              <!-- Ingresso: opcional, e por isso fecha o passo em vez de abri-lo.
                   A esmagadora maioria das aulas não pede entrada validada. -->
              <hr class="my-2"/>
              <b-form-row>
                <b-col cols="12">
                  <b-form-checkbox v-model="model.ticket_enabled" switch>
                    <strong>{{ $t('catalog.ticket.enable') }}</strong>
                  </b-form-checkbox>
                  <small class="text-muted d-block">{{ $t('catalog.ticket.enable_hint') }}</small>
                </b-col>
                <b-col v-if="model.ticket_enabled" cols="12" class="mt-2">
                  <b-form-group :label="$t('catalog.ticket.type')">
                    <choice-cards
                      :options="ticketTypeCards"
                      :value="model.ticket_type"
                      @input="value => { model.ticket_type = value }"/>
                    <small v-if="!model.ticket_type" class="text-warning d-block mt-1">{{ $t('catalog.ticket.type_required') }}</small>
                  </b-form-group>
                </b-col>
              </b-form-row>

              <div v-if="!valid" class="mt-1">
                <small class="text-warning">{{ $t('catalog.wizard.fill_required') }}</small>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- ============================== PASSO 2: HORÁRIOS E PROFESSORES -->
        <div v-show="currentStep === 'agenda'">
          <template v-if="lessonId">
            <div v-if="lessonType !== 'SGL'" class="mb-4">
              <available-schedule
                ref="scheduleGrid"
                :serviceDelegate="service"
                :entityId="lessonId"
                :defaultCapacity="model.max_student"
                :defaultDuration="model.duration"
                @dirty-change="scheduleDirty = $event"/>
            </div>
            <b-alert v-else show variant="info" class="p-1 mb-2">
              <small>{{ $t('catalog.lesson.single_event_no_grid', { date: singleEventSummary }) }}</small>
            </b-alert>

            <h6 class="mb-50">{{ $t('catalog.lesson.teachers_title') }}</h6>
            <small class="text-muted d-block mb-1">{{ $t('catalog.lesson.teachers_hint') }}</small>
            <Teacher :services="service" :lessonId="lessonId"/>
          </template>
        </div>

        <!-- ============================== PASSO 3: PREÇO E FOTOS -->
        <div v-show="currentStep === 'venda'">
          <b-alert :show="productLoadFailed" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.sale.sale_unavailable') }}</small>
          </b-alert>
          <sale-step
            v-if="productExists"
            :product-id="product.id"
            :price.sync="product.price"
            :description.sync="product.description"
            :how-works.sync="product.how_works"
            :guidelines.sync="product.guidelines"
            :files="productFiles"
            :multi-sale="productMultiSale"
            @file-uploaded="onProductFileUploaded"
            @delete-file="deleteProductFile">
            <!-- Cupons: vínculo raro, dobrado para não poluir -->
            <div class="sale-step-extra" v-if="lessonId">
              <button type="button" class="catalog-more-toggle" @click="couponsOpen = !couponsOpen">
                <feather-icon :icon="couponsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'" svgClasses="w-4 h-4" class="mr-50"/>
                {{ $t('catalog.lesson.coupons_optional') }}
              </button>
              <div v-if="couponsOpen" class="mt-1">
                <b-form-row>
                  <b-col cols="auto" class="w-full flex items-end">
                    <div class="w-full">
                      <select-suggestion
                        ref="select_coupon"
                        :max="20"
                        class="flex-grow"
                        column="name"
                        model="DiscountCoupon"
                        :label="$t('discount_coupon.label')"
                        :itemTextFormat="(item) => `${item.code} - ${item.name}`"
                        v-model="discountCouponSelected"/>
                    </div>
                    <div class="ml-1">
                      <b-button
                        variant="outline-primary"
                        v-if="discountCouponSelected"
                        @click="addDiscountCoupon">
                        <feather-icon icon="PlusIcon" class="stroke-current"/>
                      </b-button>
                    </div>
                  </b-col>
                </b-form-row>
                <vs-table :data="discountCouponList" no-data-text="Nenhum cupom vinculado" class="mt-2">
                  <template slot="thead">
                    <vs-th>Código</vs-th>
                    <vs-th>Cupom</vs-th>
                    <vs-th>Ação</vs-th>
                  </template>
                  <template slot-scope="{data}">
                    <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                      <vs-td>{{ data[indextr].code }}</vs-td>
                      <vs-td>{{ data[indextr].name }}</vs-td>
                      <vs-td>
                        <feather-icon
                          icon="Trash2Icon"
                          style="cursor: pointer;"
                          class="h-5 w-5 mr-1 stroke-current text-danger"
                          @click="deleteDiscountCoupon(data[indextr])"/>
                      </vs-td>
                    </vs-tr>
                  </template>
                </vs-table>
              </div>
            </div>
          </sale-step>
        </div>

        <!-- ============================== PASSO 4: REVISAR E PUBLICAR -->
        <div v-show="currentStep === 'revisar'">
          <b-alert :show="isExpiredSingleEvent" variant="warning" class="p-1 mb-2">
            <small>{{ $t('catalog.lesson.expired_warning') }}</small>
          </b-alert>
          <review-step
            :summary="reviewSummary"
            :price="product.price"
            :published="productPublished"
            :blockers="publishBlockers"
            :front-url="publishedFrontUrl"
            :busy="busy"
            :no-sale="planOnly"
            price-step-key="venda"
            @goto="goToStep"
            @publish="publish"
            @save-draft="saveDraft"
            @unpublish="unpublish"
            @finish="finishPlanOnly">
            <!-- Aula exclusiva de plano: no lugar de "publicado/rascunho", quais
                 planos de fato dão acesso a ela. Sem nenhum, ela não é vendida
                 por caminho nenhum — e isso precisa aparecer aqui. -->
            <template #status v-if="planOnly">
              <b-alert :show="coveringPlans.length > 0" variant="success" class="p-1 mb-2">
                <div class="flex items-start">
                  <feather-icon icon="RepeatIcon" class="mr-1 flex-shrink-0"/>
                  <div>
                    <div>{{ $tc('catalog.lesson.covered_by_plans', coveringPlans.length, { count: coveringPlans.length }) }}</div>
                    <small>{{ coveringPlans.map(function (p) { return p.name }).join(' · ') }}</small>
                  </div>
                </div>
              </b-alert>
              <b-alert :show="coveringPlansLoaded && coveringPlans.length === 0" variant="warning" class="p-1 mb-2">
                <div class="flex items-start">
                  <feather-icon icon="AlertTriangleIcon" class="mr-1 flex-shrink-0"/>
                  <small>{{ $t('catalog.lesson.no_covering_plan') }}</small>
                </div>
              </b-alert>
            </template>
          </review-step>
        </div>
      </wizard-shell>
    </vx-card>
  </div>
</template>

<script>
import {
  BAlert,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormRadioGroup,
  BButton,
  BFormRow,
  BCol,
  BFormInvalidFeedback,
  BInputGroup,
  BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, min, max } from '@validations'
import { mapGetters } from 'vuex'

import SelectSuggestion from '@/components/SelectSuggestion.vue'
import WizardShell from '@/components/catalog/WizardShell.vue'
import ChoiceCards from '@/components/catalog/ChoiceCards.vue'
import SaleStep from '@/components/catalog/SaleStep.vue'
import ReviewStep from '@/components/catalog/ReviewStep.vue'
import productBinding from '@/components/catalog/productBinding'
import { marketplaceItemUrl } from '@/components/catalog/catalog'
import { TICKET_TYPE_CARDS, ticketPayload } from '@/components/catalog/ticketTypes'

import Teacher from './Teacher'
import AvailableSchedule from './AvailableSchedule'
import LessonService from '@/services/api/LessonService'

const STEP_KEYS = ['sobre', 'agenda', 'venda', 'revisar']

/**
 * Cadastro de aula em passo a passo, com o produto embutido.
 *
 * Antes eram duas telas em dois menus (Aula e Produto > Configuração) e no
 * mínimo seis saves para uma aula comprável — e nada aqui dizia que a aula
 * precisava de um produto para ser vendida. Agora o produto é criado como
 * rascunho por baixo no passo "Preço e fotos", vinculado 1↔1 (trava só do
 * front, por decisão de 2026-08-15) e publicado no passo final.
 *
 * A rota continua a mesma (create → replace para edit após o 1º save; o Vue
 * reaproveita a instância, então o estado do passo sobrevive). O passo atual
 * vive em ?step= para F5/link direto funcionarem — inclusive o "Continuar
 * cadastro" da listagem, que manda para ?step=venda.
 */
export default {
  mixins: [productBinding],
  components: {
    SelectSuggestion,
    WizardShell,
    ChoiceCards,
    SaleStep,
    ReviewStep,
    BAlert,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormRadioGroup,
    BButton,
    BFormRow,
    BFormInvalidFeedback,
    BCol,
    BInputGroup,
    BFormCheckbox,
    ValidationObserver,
    ValidationProvider,
    Teacher,
    AvailableSchedule,
  },

  props: {
    id: {
      type: [Number, String],
      default: null,
    },
  },

  data: () => ({
    service: null,
    currentStep: 'sobre',
    busy: false,
    /**
     * Id da aula recém-criada, antes de a rota (e a prop `id`) refletirem o
     * replace create→edit. Sem ele, o enterStep seguinte via a prop ainda nula
     * e devolvia o usuário ao passo 1.
     */
    internalId: null,
    moreOptionsOpen: false,
    couponsOpen: false,
    scheduleDirty: false,
    reviewCounts: { schedules: null, teachers: null },
    /** Aula vendida só dentro de plano: sem anúncio avulso, sem preço próprio. */
    planOnly: false,
    coveringPlans: [],
    coveringPlansLoaded: false,
    lessonType: null,
    singleEventDate: null,
    singleEventStartTime: null,
    singleEventEndTime: null,
    model: {
      name: '',
      max_student: null,
      min_purchase_advance_hours: null,
      duration: null,
      quantity: null,
      description: '',
      ticket_enabled: false,
      ticket_type: null,
    },
    modalitySelected: null,
    rentalSpaceSelected: null,
    legalEntity: null,
    discountCouponSelected: null,
    discountCouponList: [],
  }),

  computed: {
    ...mapGetters('dataAccess', ['activeEntity', 'entities', 'loaded', 'globalInstitutionLocked']),
    lessonId() {
      if (this.internalId) return this.internalId
      return this.id === null || this.id === undefined || this.id === '' ? null : Number(this.id)
    },
    isEditMode() {
      return this.lessonId !== null
    },
    pessoaJuridicaLocked() {
      return this.globalInstitutionLocked || (Array.isArray(this.entities) && this.entities.length === 1)
    },
    /** Sem venda avulsa não existe passo de preço/fotos — nada seria vendido ali. */
    stepKeys() {
      return this.planOnly
        ? STEP_KEYS.filter(key => key !== 'venda')
        : STEP_KEYS
    },
    steps() {
      const all = [
        { key: 'sobre', title: this.$t('catalog.lesson.step_about'), hint: this.$t('catalog.lesson.step_about_hint') },
        {
          key: 'agenda',
          title: this.lessonType === 'SGL' ? this.$t('catalog.lesson.step_teachers') : this.$t('catalog.lesson.step_agenda'),
          hint: this.lessonType === 'SGL' ? null : this.$t('catalog.lesson.step_agenda_hint'),
        },
        { key: 'venda', title: this.$t('catalog.sale.step_title'), hint: this.$t('catalog.sale.step_hint') },
        {
          key: 'revisar',
          title: this.planOnly ? this.$t('catalog.lesson.step_review_plan_only') : this.$t('catalog.review.step_title'),
          hint: this.planOnly ? this.$t('catalog.lesson.step_review_plan_only_hint') : this.$t('catalog.review.step_hint'),
        },
      ]
      return all.filter(step => this.stepKeys.includes(step.key))
    },
    salesModeCards() {
      return [
        {
          value: 'OPEN',
          title: 'Avulsa e em planos',
          description: 'O aluno pode comprar esta aula sozinha, e ela também vale para quem tem plano que a inclua.',
          example: 'Ex.: aula experimental que qualquer um compra',
          icon: 'ShoppingCartIcon',
        },
        {
          value: 'PLAN',
          title: 'Somente em planos',
          description: 'Não aparece à venda no marketplace. Só quem assina um plano que inclui esta aula consegue agendar.',
          example: 'Ex.: turma fechada de alunos mensalistas',
          icon: 'RepeatIcon',
        },
      ]
    },
    doneKeys() {
      // Em edição a navegação é livre; aqui só marca o que já tem substância.
      const done = []
      if (this.lessonId) done.push('sobre', 'agenda')
      if (this.productExists && !this.planOnly) done.push('venda')
      return done
    },
    nextDisabled() {
      if (this.currentStep === 'sobre') return !this.lessonType
      return false
    },
    /** Compartilhado com o wizard de espaço — ver components/catalog/ticketTypes. */
    ticketTypeCards() {
      return TICKET_TYPE_CARDS
    },
    lessonTypeCards() {
      return [
        {
          value: 'SGL',
          title: 'Evento único',
          description: 'Uma data e horário só. Bom para workshop, avaliação ou aula experimental.',
          example: 'Ex.: Workshop de mobilidade — 20/09, 9h às 11h',
          icon: 'CalendarIcon',
        },
        {
          value: 'REC',
          title: 'Recorrente',
          description: 'Acontece toda semana nos horários da grade. O aluno compra e agenda as sessões.',
          example: 'Ex.: Pilates às terças e quintas',
          icon: 'RepeatIcon',
        },
        {
          value: 'PKG',
          title: 'Pacote fixo',
          description: 'Quantidade fechada de sessões que o aluno usa aos poucos, nos horários da grade.',
          example: 'Ex.: Pacote de 10 sessões de RPG',
          icon: 'LayersIcon',
        },
      ]
    },
    singleEventSummary() {
      if (!this.singleEventDate) return ''
      const [year, month, day] = String(this.singleEventDate).split('-')
      const time = this.singleEventStartTime ? ` às ${this.singleEventStartTime}` : ''
      return `${day}/${month}/${year}${time}`
    },
    isExpiredSingleEvent() {
      if (this.lessonType !== 'SGL' || !this.singleEventDate) return false
      const reference = new Date(`${this.singleEventDate}T${this.singleEventEndTime || this.singleEventStartTime || '23:59'}:00`)
      return !Number.isNaN(reference.getTime()) && reference.getTime() < Date.now()
    },
    lessonTypeLabel() {
      const card = this.lessonTypeCards.find(item => item.value === this.lessonType)
      return card ? card.title : ''
    },
    publishBlockers() {
      const blockers = []
      // Grade vazia impede os dois modos: sem horário não há o que agendar,
      // nem para quem compra avulso nem para quem tem plano.
      if (this.lessonType !== 'SGL' && this.reviewCounts.schedules === 0) {
        blockers.push({ text: this.$t('catalog.lesson.blocker_schedules'), stepKey: 'agenda' })
      }
      // Exclusiva de plano não tem anúncio: preço e publicação não se aplicam.
      // Não estar em nenhum plano é aviso (o clube pode montar o plano depois),
      // não impedimento — daí ficar fora desta lista.
      if (this.planOnly) {
        return blockers
      }
      if (!this.productExists) {
        blockers.push({ text: this.$t('catalog.lesson.blocker_sale_step'), stepKey: 'venda' })
      }
      if (this.productExists && (this.product.price === null || this.product.price === '')) {
        blockers.push({ text: this.$t('catalog.lesson.blocker_price'), stepKey: 'venda' })
      }
      if (this.productLoadFailed) {
        blockers.push({ text: this.$t('catalog.sale.sale_unavailable') })
      }
      return blockers
    },
    publishedFrontUrl() {
      return !this.planOnly && this.productPublished ? marketplaceItemUrl('LES', this.product.id) : null
    },
    reviewSummary() {
      const aboutRows = [
        { label: this.$t('lesson.name'), value: this.model.name },
        { label: this.$t('modality.label'), value: this.modalitySelected && this.modalitySelected.name },
        { label: this.$t('lesson.type'), value: this.lessonTypeLabel },
        {
          label: this.$t('catalog.lesson.sales_mode'),
          value: this.planOnly
            ? this.$t('catalog.status.plan_only')
            : this.$t('catalog.lesson.sales_mode_open_short'),
        },
      ]
      if (this.lessonType === 'SGL') {
        aboutRows.push({ label: this.$t('catalog.lesson.review_when'), value: this.singleEventSummary })
      }
      if (this.lessonType === 'REC') {
      }
      if (this.lessonType === 'PKG') {
        aboutRows.push({ label: this.$t('lesson.quantity'), value: this.model.quantity })
      }
      aboutRows.push({ label: this.$t('lesson.duration'), value: this.model.duration ? `${this.model.duration} min` : null })
      aboutRows.push({ label: this.$t('lesson.max_student'), value: this.model.max_student })

      const agendaRows = []
      if (this.lessonType !== 'SGL') {
        agendaRows.push({
          label: this.$t('catalog.lesson.review_schedules'),
          value: this.reviewCounts.schedules === null ? '…' : this.reviewCounts.schedules,
          missing: this.reviewCounts.schedules === 0,
        })
      }
      agendaRows.push({
        label: this.$t('catalog.lesson.review_teachers'),
        value: this.reviewCounts.teachers === null ? '…' : this.reviewCounts.teachers,
      })

      const sections = [
        { key: 'sobre', title: this.$t('catalog.lesson.step_about'), icon: 'BookOpenIcon', stepKey: 'sobre', rows: aboutRows },
        { key: 'agenda', title: this.$t('catalog.lesson.step_agenda'), icon: 'ClockIcon', stepKey: 'agenda', rows: agendaRows },
      ]

      // Sem anúncio não há foto de anúncio: a aula exclusiva de plano aparece
      // para o assinante dentro do plano, que tem a própria imagem.
      if (!this.planOnly) {
        sections.push({
          key: 'fotos',
          title: this.$t('catalog.sale.photos'),
          icon: 'ImageIcon',
          stepKey: 'venda',
          rows: [{
            label: this.$t('catalog.sale.photos'),
            value: this.productFiles.length
              ? this.$tc('catalog.lesson.review_photos_count', this.productFiles.length, { count: this.productFiles.length })
              : this.$t('catalog.lesson.review_photos_none'),
          }],
        })
      }

      return sections
    },
  },

  watch: {
    '$route.query.step'(value) {
      const step = this.stepKeys.includes(value) ? value : 'sobre'
      if (step !== this.currentStep) {
        this.enterStep(step)
      }
    },
    modalitySelected(val) {
      if (val && this.$refs.modalityProvider) {
        this.$nextTick(() => this.$refs.modalityProvider.validate())
      }
    },
  },

  methods: {
    legalEntityItemTextFormat(model) {
      const trading = model?.trading_name || ''
      const company = model?.company_name || ''
      const label = trading && company ? `${trading} - ${company}` : (trading || company)
      return label || model?.name || `ID: ${model?.id}`
    },
    prefillLegalEntity() {
      if (this.legalEntity || this.lessonId) {
        return
      }
      if (this.activeEntity) {
        this.legalEntity = { id: this.activeEntity.id, trading_name: this.activeEntity.name || '', company_name: '' }
        return
      }
      if (Array.isArray(this.entities) && this.entities.length === 1) {
        const [entity] = this.entities
        this.legalEntity = { id: entity.id, trading_name: entity.trading_name || entity.name || '', company_name: entity.company_name || '' }
      }
    },
    getValidationState({ invalid, validated }) {
      const valid = !invalid
      return !validated ? null : valid
    },

    // ------------------------------------------------------------------
    // Navegação entre passos
    // ------------------------------------------------------------------
    stepIndex(key) {
      return this.stepKeys.indexOf(key)
    },
    syncStepQuery(step) {
      if (this.$route.query.step !== step) {
        this.$router.replace({ query: { ...this.$route.query, step } }).catch(() => {})
      }
    },
    async goNext() {
      const index = this.stepIndex(this.currentStep)
      const next = this.stepKeys[index + 1]
      if (!next) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(next)
    },
    goPrev() {
      const index = this.stepIndex(this.currentStep)
      const prev = this.stepKeys[index - 1]
      if (prev) this.goToStep(prev)
    },
    async goToStep(step) {
      if (step === this.currentStep) return
      const left = await this.leaveStep(this.currentStep)
      if (!left) return
      await this.enterStep(step)
    },

    /** Sair de um passo salva o que aquele passo edita. false = ficar. */
    async leaveStep(step) {
      if (step === 'sobre') {
        const valid = await this.$refs.validator.validate()
        if (!valid) {
          this.notifyWarning(this.$vs, this.$t('catalog.wizard.fill_required'))
          return false
        }
        return this.saveLessonCore()
      }
      if (step === 'agenda') {
        const grid = this.$refs.scheduleGrid
        if (grid && grid.hasErrors) {
          this.notifyWarning(this.$vs, this.$t('catalog.lesson.grid_fix_errors'))
          return false
        }
        if (grid && grid.isDirty) {
          // Salva em bloco pelo próprio componente da grade (diff explícito).
          await grid.save()
          if (grid.isDirty) return false
        }
        return true
      }
      return true
    },

    async enterStep(step) {
      // Sem aula salva só existe o primeiro passo.
      if (!this.lessonId && step !== 'sobre') {
        step = 'sobre'
      }
      // Passo de venda não existe para aula exclusiva de plano (link antigo,
      // ?step=venda salvo, ou troca do modo com o passo já aberto).
      if (!this.stepKeys.includes(step)) {
        step = 'revisar'
      }
      // Reentrada (watcher da query + goNext chegam juntos no create→edit).
      if (step === this.currentStep && this.$route.query.step === step) {
        return
      }
      if (step === 'venda' && this.lessonId) {
        const ready = await this.ensureSale()
        if (!ready) return
      }
      if (step === 'revisar' && this.lessonId) {
        this.refreshReviewCounts()
        if (this.planOnly) {
          this.refreshCoveringPlans()
        } else if (!this.productExists && !this.productLoadFailed) {
          // Chegou direto por link: tenta montar a parte de venda em silêncio.
          await this.ensureSale()
        }
      }
      this.currentStep = step
      this.syncStepQuery(step)
    },

    // ------------------------------------------------------------------
    // Persistência
    // ------------------------------------------------------------------
    lessonPayload() {
      const isSingleEvent = this.lessonType === 'SGL'
      return {
        id: this.lessonId,
        name: this.model.name,
        max_student: this.model.max_student,
        min_purchase_advance_hours: this.model.min_purchase_advance_hours === '' ? null : this.model.min_purchase_advance_hours,
        duration: this.model.duration,
        type: this.lessonType,
        quantity: this.lessonType === 'PKG' ? this.model.quantity : null,
        start_at: isSingleEvent ? this.buildSingleEventDateTime(this.singleEventStartTime) : null,
        end_at: isSingleEvent ? this.buildSingleEventDateTime(this.singleEventEndTime) : null,
        description: this.model.description,
        plan_only: this.planOnly,
        ...ticketPayload(this.model),
        modality_id: this.modalitySelected ? this.modalitySelected.id : null,
        rental_space_id: this.rentalSpaceSelected ? this.rentalSpaceSelected.id : null,
        legal_entity_id: this.legalEntity ? this.legalEntity.id : null,
      }
    },
    async saveLessonCore() {
      this.busy = true
      this.$vs.loading()
      try {
        const wasCreate = !this.lessonId
        const data = await this.service.createOrUpdate(this.lessonPayload())
        if (wasCreate) {
          this.internalId = data.id
          // Mesma instância do componente (rota-componente igual): o estado do
          // wizard sobrevive ao replace, e F5 volta direto no passo da query.
          // AGUARDADO de propósito: o guard global roda v1/logged de forma
          // assíncrona, e disparar outra navegação nesse meio-tempo cancela as
          // duas — o replace seguinte do syncStepQuery matava este aqui.
          await this.$router.replace(`/lessons/${data.id}/edit?step=agenda`).catch(() => {})
        }
        return true
      } catch (error) {
        this.showErrors(this.$refs.validator, error, this)
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async ensureSale() {
      // Aula exclusiva de plano não tem anúncio — e não deve ganhar um.
      if (this.planOnly || !this.lessonId || !this.legalEntity) {
        return false
      }
      if (this.productExists) {
        return true
      }
      this.busy = true
      this.$vs.loading()
      try {
        await this.ensureProduct({
          itemType: 'LES',
          itemId: this.lessonId,
          itemName: this.model.name,
          legalEntityId: this.legalEntity.id,
        })
        this.productLoadFailed = false
        return true
      } catch (error) {
        // 'sale-unavailable' é a recusa deliberada de criar anúncio às cegas
        // depois de uma leitura falha; a tela já explica com o alerta do passo.
        if (!error || error.message !== 'sale-unavailable') {
          this.notifyErrors(this.$vs, error)
        }
        this.productLoadFailed = true
        return false
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    /**
     * Troca entre "vendida avulsa" e "somente em planos".
     *
     * O anúncio já criado não é apagado aqui: se o clube voltar atrás, o preço e
     * as fotos continuam onde estavam. O que o servidor recusa é publicar (ou
     * manter publicado) o anúncio de uma aula marcada como exclusiva de plano.
     */
    onChangeSalesMode(value) {
      this.planOnly = value === 'PLAN'
    },
    async refreshCoveringPlans() {
      if (!this.lessonId) return
      try {
        const plans = await this.service.getCoveringPlans(this.lessonId)
        this.coveringPlans = Array.isArray(plans) ? plans : []
      } catch (error) {
        this.coveringPlans = []
      } finally {
        this.coveringPlansLoaded = true
      }
    },
    /** Aula exclusiva de plano: não há o que publicar, só gravar e sair. */
    async finishPlanOnly() {
      this.busy = true
      this.$vs.loading()
      try {
        await this.service.createOrUpdate(this.lessonPayload())
        this.notifySuccess(this.$vs, this.$t('catalog.lesson.plan_only_saved'))
        this.$router.push('/lessons').catch(() => {})
      } catch (error) {
        this.notifyErrors(this.$vs, error)
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },
    async refreshReviewCounts() {
      if (!this.lessonId) return
      try {
        const [schedules, teachers] = await Promise.all([
          this.lessonType !== 'SGL' ? this.service.getAllAvailableSchedules(this.lessonId) : Promise.resolve(null),
          this.service.getAllTeachers(this.lessonId),
        ])
        this.reviewCounts = {
          schedules: Array.isArray(schedules) ? schedules.length : null,
          teachers: Array.isArray(teachers) ? teachers.length : 0,
        }
      } catch (error) {
        this.reviewCounts = { schedules: null, teachers: null }
      }
    },

    // ------------------------------------------------------------------
    // Publicação
    // ------------------------------------------------------------------
    async publish() {
      if (this.productNeedsPriceConfirmation()) {
        this.confirmProductPriceChange(() => this.publish())
        return
      }
      await this.persistAll('RE', this.$t('catalog.review.published_success'))
    },
    async saveDraft() {
      const status = this.product.status && this.product.status !== 'RE' ? this.product.status : 'CR'
      await this.persistAll(status, this.$t('catalog.review.draft_saved'))
    },
    async unpublish() {
      await this.persistAll('DI', this.$t('catalog.review.unpublished_success'))
    },
    /** Regrava aula + produto juntos: o resumo da revisão é o que vai ao ar. */
    async persistAll(status, successMessage) {
      this.busy = true
      this.$vs.loading()
      try {
        await this.service.createOrUpdate(this.lessonPayload())
        await this.saveProductAs(status, {
          itemName: this.model.name,
          legalEntityId: this.legalEntity ? this.legalEntity.id : null,
        })
        this.notifySuccess(this.$vs, successMessage)
      } catch (error) {
        this.notifyErrors(this.$vs, error)
      } finally {
        this.$vs.loading.close()
        this.busy = false
      }
    },

    // ------------------------------------------------------------------
    // Tipo / datas do evento único
    // ------------------------------------------------------------------
    onChangeLessonType(value) {
      if (this.isEditMode) return
      this.lessonType = value
      this.model.quantity = null
      this.singleEventDate = null
      this.singleEventStartTime = null
      this.singleEventEndTime = null
      if (this.$refs.validator) {
        this.$refs.validator.reset()
      }
    },
    splitDateTime(value) {
      if (!value) {
        return { date: null, time: null }
      }
      const [date, time] = String(value).replace('T', ' ').split(' ')
      return { date, time: time ? time.slice(0, 5) : null }
    },
    buildSingleEventDateTime(time) {
      if (!this.singleEventDate || !time) {
        return null
      }
      return `${this.singleEventDate}T${time}:00`
    },

    // ------------------------------------------------------------------
    // Carga
    // ------------------------------------------------------------------
    async loadData(id) {
      this.$vs.loading()
      try {
        const response = await this.service.read(id)
        this.model = {
          name: response.name || '',
          max_student: response.max_student,
          min_purchase_advance_hours: response.min_purchase_advance_hours,
          duration: response.duration,
          quantity: response.quantity,
          description: response.description || '',
          ticket_enabled: !!response.ticket_enabled,
          ticket_type: response.ticket_type || null,
        }
        this.lessonType = this._.get(response, 'type', null)
        this.planOnly = !!response.plan_only
        if (this.lessonType === 'SGL') {
          this.singleEventDate = this.splitDateTime(response.start_at).date
          this.singleEventStartTime = this.splitDateTime(response.start_at).time
          this.singleEventEndTime = this.splitDateTime(response.end_at).time
        }
        this.modalitySelected = { id: response.modality_id, name: response.modality_name }
        this.rentalSpaceSelected = response.rental_space_id
          ? { id: response.rental_space_id, name: response.rental_space_name || '' }
          : null
        if (response.rental_space_id || response.min_purchase_advance_hours) {
          this.moreOptionsOpen = true
        }
        if (response.legal_entity) {
          this.legalEntity = response.legal_entity
        } else if (response.legal_entity_id) {
          const match = (this.entities || []).find(entity => entity.id === response.legal_entity_id)
          this.legalEntity = match || { id: response.legal_entity_id, trading_name: '', company_name: '' }
        }
      } finally {
        this.$vs.loading.close()
      }
    },
    loadDataDiscountCoupon() {
      this.service.getAllDiscountCoupon(this.lessonId).then(
        response => { this.discountCouponList = Array.from(response) },
        () => {}
      )
    },
    addDiscountCoupon() {
      this.$vs.loading()
      this.service.addDiscountCoupon(this.lessonId, [this.discountCouponSelected.id]).then(
        () => {
          this.notifySuccess(this.$vs, this.$t('messages.success'))
          this.discountCouponSelected = null
          this.$refs.select_coupon?.reset()
          this.loadDataDiscountCoupon()
        },
        error => { this.notifyErrors(this.$vs, error) }
      ).finally(() => { this.$vs.loading.close() })
    },
    deleteDiscountCoupon(entity) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'success',
        title: 'Confirmação',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: 'Tem certeza que deseja excluir este registro?',
        accept: () => {
          this.$vs.loading()
          this.service.deleteDiscountCoupon(this.lessonId, entity.id).then(
            () => {
              this.discountCouponList = this.discountCouponList.filter(element => element.id !== entity.id)
              this.notifySuccess(this.$vs)
            },
            error => { this.notifyErrors(this.$vs, error) }
          ).finally(() => { this.$vs.loading.close() })
        },
      })
    },
  },

  beforeRouteLeave(to, from, next) {
    const stayingInForm = (to.name === 'lesson-edit' || to.name === 'lesson-create')
      && String(to.params.id || '') === String(this.lessonId || '')
    const priceDirty = this.productExists
      && this.productOriginalPrice !== null
      && Number(this.product.price) !== Number(this.productOriginalPrice)
    if (!stayingInForm && (priceDirty || this.scheduleDirty)) {
      if (!window.confirm(this.$t('catalog.wizard.unsaved_leave_confirm'))) {
        next(false)
        return
      }
    }
    next()
  },

  beforeMount() {
    this.service = LessonService.build(this.$vs)
  },

  async mounted() {
    if (!this.loaded) {
      await this.$store.dispatch('dataAccess/setupFromLogin')
    }
    this.prefillLegalEntity()

    if (this.lessonId) {
      await this.loadData(this.lessonId)
      this.loadDataDiscountCoupon()
      // Aula exclusiva de plano não tem anúncio para carregar.
      if (!this.planOnly) {
        await this.loadLinkedProduct('LES', this.lessonId, this.model.name)
      }
    }

    const requested = this.$route.query.step
    const initial = this.stepKeys.includes(requested) ? requested : 'sobre'
    await this.enterStep(initial)
  },
}
</script>

<style lang="scss" scoped>
.catalog-more-toggle {
  display: flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  font-weight: 600;
  cursor: pointer;
  color: inherit;
  margin-top: 0.5rem;
}

.sale-step-extra {
  padding-top: 1rem;
  border-top: 1px dashed rgba(0, 0, 0, 0.1);
}

::v-deep label.required::before {
  content: '* ';
  color: #ff9f43;
}
</style>
